import React, { useState, useMemo } from 'react';
import { 
  Plus, Search, AlertTriangle, FileBadge, Car, History, Coins, Edit2, Trash2, Globe,
  Users, CreditCard, CheckCircle, DollarSign, X, Check, AlertCircle 
} from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { useCollection } from '../../../hooks/useCollection';

import ClientForm from './ClientForm';
import ClientPaymentModal from './ClientPaymentModal';
import ClientHistoryModal from './ClientHistoryModal';
import DocumentViewerModal from '../../shared/components/DocumentViewerModal';
import BlacklistWarningModal from '../../blacklist/components/BlacklistWarningModal';
import { matchClient, logAudit } from '../../blacklist/utils/blacklistService';
import { useAuth } from '../../../context/AuthContext';

export default function ClientsPage() {
  const { lang, t } = useLanguage();
  const isAr = lang === 'ar';
  const { agencyId } = useAuth();

  const { data: clients, loading, add, update, remove, refresh } = useCollection('clients');
  // Global blacklist (shared, realtime, read-only here) for new-client verification.
  const { data: blacklist } = useCollection('blacklist', { bypassAgencyFilter: true });
  const [blCheck, setBlCheck] = useState(null); // { matches, payload, editingId }

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [paymentClient, setPaymentClient] = useState(null);
  const [historyClient, setHistoryClient] = useState(null);
  const [viewDoc, setViewDoc] = useState(null);
  const [filterCredit, setFilterCredit] = useState('all'); // 'all' | 'withCredit' | 'noCredit'

  // Apply filters
  const filtered = useMemo(() => {
    let result = clients;
    
    if (filterCredit === 'withCredit') {
      result = result.filter(c => (c.creditBalance || 0) > 0);
    } else if (filterCredit === 'noCredit') {
      result = result.filter(c => (c.creditBalance || 0) <= 0);
    }
    
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.phone || '').includes(search) ||
        (c.cin || '').toLowerCase().includes(q)
      );
    }
    
    return result;
  }, [clients, search, filterCredit]);
  
  // Stats
  const stats = useMemo(() => {
    const withCredit = clients.filter(c => (c.creditBalance || 0) > 0);
    const totalCredit = withCredit.reduce((sum, c) => sum + (c.creditBalance || 0), 0);
    return {
      total: clients.length,
      withCredit: withCredit.length,
      noCredit: clients.length - withCredit.length,
      totalCreditAmount: totalCredit
    };
  }, [clients]);

  const handleDelete = async (id) => {
    if (window.confirm(t('confirm_delete_client'))) {
      try {
        await remove(id);
      } catch (e) {
        alert('❌ Erreur: ' + e.message);
      }
    }
  };

  const doSaveClient = async (payload, editingId) => {
    if (editingId) await update(editingId, payload);
    else await add(payload);
  };

  const handleSave = async (payload) => {
    // Automatic global-blacklist verification when entering a new/edited client.
    try {
      const asClient = {
        clientCin: payload.cin, clientPassport: payload.passport, clientPermis: payload.permis,
        clientPhone: payload.phone, clientEmail: payload.email, clientName: payload.name,
        clientBirthDate: payload.birthDate, clientCity: payload.city,
      };
      const matches = matchClient(asClient, blacklist || []);
      if (matches.length) {
        logAudit('viewed', { actor: agencyId, agencyId, targetName: payload.name, details: { context: 'client_check', matched: matches.length } });
        setBlCheck({ matches, payload, editingId: editing?.id || null });
        return; // defer save until the user decides
      }
    } catch (_) { /* verification must never block a clean client */ }
    await doSaveClient(payload, editing?.id);
  };

  // Skeleton Loader for Modern Dashboard Feel
  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Header Skeleton */}
        <div className="flex justify-between items-center mb-6">
          <div className="space-y-2">
            <div className="h-8 w-48 bg-gray-200 rounded-lg" />
            <div className="h-4 w-64 bg-gray-100 rounded" />
          </div>
          <div className="h-11 w-36 bg-gray-200 rounded-xl" />
        </div>
        
        {/* Stats Grid Skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[1, 2, 3, 4].map(n => (
            <div key={n} className="p-5 rounded-2xl border border-gray-100 bg-white space-y-3 shadow-sm">
              <div className="flex justify-between items-center">
                <div className="h-4 w-24 bg-gray-250 bg-gray-200 rounded" />
                <div className="h-8 w-8 bg-gray-100 rounded-xl" />
              </div>
              <div className="h-8 w-16 bg-gray-200 rounded-lg" />
              <div className="h-3.5 w-32 bg-gray-100 rounded" />
            </div>
          ))}
        </div>

        {/* Search Bar Skeleton */}
        <div className="h-14 w-full bg-white border border-gray-100 rounded-2xl shadow-sm" />

        {/* Table Skeleton */}
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="bg-gray-50 h-11 border-b border-gray-200" />
          <div className="p-5 space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="flex justify-between items-center py-2 border-b border-gray-50">
                <div className="h-5 w-32 bg-gray-100 rounded" />
                <div className="h-4 w-16 bg-gray-100 rounded" />
                 <div className="h-4 w-28 bg-gray-100 rounded" />
                <div className="h-4 w-20 bg-gray-100 rounded" />
                <div className="h-6 w-20 bg-gray-100 rounded-lg" />
                <div className="flex gap-2">
                  <div className="h-8 w-8 bg-gray-100 rounded-lg" />
                  <div className="h-8 w-8 bg-gray-100 rounded-lg" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const hasActiveFilters = filterCredit !== 'all' || search.trim() !== '';

  return (
    <div className="space-y-6">
      {blCheck && (
        <BlacklistWarningModal
          matches={blCheck.matches}
          onCancel={() => {
            logAudit('rejection', { actor: agencyId, agencyId, targetName: blCheck.payload?.name, details: { context: 'client_cancelled' } });
            setBlCheck(null);
          }}
          onContinue={async () => {
            const { payload, matches, editingId } = blCheck;
            await logAudit('override', {
              actor: agencyId, agencyId, targetName: payload?.name,
              targetId: matches[0]?.record?.id || '',
              details: { context: 'client_override', matchedReasons: matches.map(m => m.reasons).flat(), risk: matches[0]?.record?.riskLevel },
            });
            setBlCheck(null);
            setShowForm(false);
            await doSaveClient(payload, editingId);
          }}
        />
      )}
      {/* Header section */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{t('clients_title')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isAr 
              ? `إدارة زبائن الوكالة ومتابعة ديونهم وتأجيراتهم (${clients.length} عميل)` 
              : `Gérez la base de données de vos clients et suivez leurs paiements (${clients.length} clients)`}
          </p>
        </div>
        <button 
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl font-semibold shadow-md transition-all duration-200 hover:shadow-lg active:scale-[0.98]"
        >
          <Plus className="w-5 h-5" /> 
          {t('add_client')}
        </button>
      </div>
      
      {/* MODERN STATS widgets */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <button 
          onClick={() => setFilterCredit('all')}
          className={`p-5 rounded-2xl border text-start transition-all duration-200 relative overflow-hidden group ${
            filterCredit === 'all' 
              ? 'bg-blue-50/50 border-blue-500/30 ring-2 ring-blue-500/10 shadow-sm' 
              : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-sm'
          }`}
        >
          <div className="flex justify-between items-start mb-3">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">{isAr ? 'الكل' : 'Tous les clients'}</span>
            <div className={`p-2 rounded-xl transition-colors duration-200 ${filterCredit === 'all' ? 'bg-blue-500/10 text-blue-600' : 'bg-gray-100 text-gray-500 group-hover:bg-blue-500/10 group-hover:text-blue-600'}`}>
              <Users className="w-5 h-5" />
            </div>
          </div>
          <div className="text-3xl font-black text-gray-900 leading-none">{stats.total}</div>
          <div className="text-xs text-gray-500 mt-2 font-medium">{isAr ? 'إجمالي العملاء المسجلين' : 'Clients enregistrés'}</div>
        </button>

        <button 
          onClick={() => setFilterCredit('withCredit')}
          className={`p-5 rounded-2xl border text-start transition-all duration-200 relative overflow-hidden group ${
            filterCredit === 'withCredit' 
              ? 'bg-rose-50/50 border-rose-500/30 ring-2 ring-rose-500/10 shadow-sm' 
              : 'bg-white border-gray-200 hover:border-rose-300 hover:shadow-sm'
          }`}
        >
          <div className="flex justify-between items-start mb-3">
            <span className="text-xs font-bold text-rose-700 uppercase tracking-wider">{isAr ? 'بدين' : 'Avec crédit'}</span>
            <div className={`p-2 rounded-xl transition-colors duration-200 ${filterCredit === 'withCredit' ? 'bg-rose-500/10 text-rose-600' : 'bg-gray-100 text-gray-500 group-hover:bg-rose-500/10 group-hover:text-rose-600'}`}>
              <CreditCard className="w-5 h-5" />
            </div>
          </div>
          <div className="text-3xl font-black text-rose-900 leading-none">{stats.withCredit}</div>
          <div className="text-xs text-rose-600 mt-2 font-medium">{isAr ? 'عملاء بذمة مالية' : 'Comptes débiteurs'}</div>
        </button>

        <button 
          onClick={() => setFilterCredit('noCredit')}
          className={`p-5 rounded-2xl border text-start transition-all duration-200 relative overflow-hidden group ${
            filterCredit === 'noCredit' 
              ? 'bg-emerald-50/50 border-emerald-500/30 ring-2 ring-emerald-500/10 shadow-sm' 
              : 'bg-white border-gray-200 hover:border-emerald-300 hover:shadow-sm'
          }`}
        >
          <div className="flex justify-between items-start mb-3">
            <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">{isAr ? 'بدون دين' : 'Sans crédit'}</span>
            <div className={`p-2 rounded-xl transition-colors duration-200 ${filterCredit === 'noCredit' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-gray-100 text-gray-500 group-hover:bg-emerald-500/10 group-hover:text-emerald-600'}`}>
              <CheckCircle className="w-5 h-5" />
            </div>
          </div>
          <div className="text-3xl font-black text-emerald-900 leading-none">{stats.noCredit}</div>
          <div className="text-xs text-emerald-600 mt-2 font-medium">{isAr ? 'حسابات مسواة بالكامل' : 'Comptes en règle'}</div>
        </button>

        <div className="p-5 rounded-2xl border border-amber-200 bg-amber-50/50 relative overflow-hidden">
          <div className="flex justify-between items-start mb-3">
            <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">{isAr ? 'إجمالي الدين' : 'Total crédit'}</span>
            <div className="p-2 rounded-xl bg-amber-500/10 text-amber-600">
              <DollarSign className="w-5 h-5" />
            </div>
          </div>
          <div className="text-3xl font-black text-amber-900 leading-none font-mono">
            {stats.totalCreditAmount.toLocaleString()} <span className="text-sm font-bold">MAD</span>
          </div>
          <div className="text-xs text-amber-600 mt-2 font-medium">{isAr ? 'مستحقات غير محصلة' : 'Créance globale en attente'}</div>
        </div>
      </div>
      
      {/* Search & Actions Panel */}
      <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm flex flex-col md:flex-row gap-4 justify-between items-center">
        <div className="relative w-full md:max-w-md">
          <Search className={`absolute ${isAr ? 'right-3.5' : 'left-3.5'} top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400`} />
          <input 
            type="text" 
            value={search} 
            onChange={e => setSearch(e.target.value)}
            placeholder={t('search_clients_placeholder') || (isAr ? 'البحث عن عميل (الاسم، البطاقة، الهاتف)...' : 'Rechercher un client (Nom, CIN, Tél)...')}
            className={`w-full ${isAr ? 'pr-11 pl-10' : 'pl-11 pr-10'} py-2.5 bg-gray-50/50 border border-gray-200 rounded-xl focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/5 transition-all outline-none text-sm`}
          />
          {search && (
            <button 
              onClick={() => setSearch('')}
              className={`absolute ${isAr ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        
        {/* Active filter display inside the actions panel */}
        {filterCredit !== 'all' && (
          <div className="flex items-center gap-2 self-start md:self-center bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-xl text-xs font-semibold text-gray-700">
            <span>{isAr ? 'الفلتر النشط: ' : 'Filtre : '}</span>
            <span className={`px-2 py-0.5 rounded-lg font-bold ${filterCredit === 'withCredit' ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-green-50 text-green-700 border border-green-100'}`}>
              {filterCredit === 'withCredit' ? (isAr ? 'بدين' : 'Avec crédit') : (isAr ? 'بدون دين' : 'Sans crédit')}
            </span>
            <button
              onClick={() => setFilterCredit('all')}
              className="p-0.5 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-650 transition"
              title={isAr ? 'إزالة الفلتر' : 'Supprimer le filtre'}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
      
      {/* Empty State vs Table Conditional rendering */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-250 border-gray-200 shadow-sm p-12 text-center max-w-xl mx-auto my-8">
          <div className="w-16 h-16 bg-blue-50 text-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-transform hover:scale-105">
            {hasActiveFilters ? (
              <AlertCircle className="w-8 h-8" />
            ) : (
              <Users className="w-8 h-8" />
            )}
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-1">
            {hasActiveFilters 
              ? (isAr ? 'لا توجد نتائج مطابقة' : 'Aucun client trouvé')
              : (isAr ? 'قائمة العملاء فارغة' : 'Aucun client enregistré')}
          </h3>
          <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto leading-relaxed">
            {hasActiveFilters 
              ? (isAr ? 'لم نجد أي عميل يطابق معايير البحث أو الفلترة التي حددتها. جرب تغيير الكلمات المفتاحية أو إزالة الفلاتر.' : 'Nous n\'avons trouvé aucun client correspondant à vos critères de recherche ou de filtre. Veuillez modifier vos termes ou réinitialiser les filtres.')
              : (isAr ? 'ابدأ بإضافة أول عميل إلى نظامك لإدارة الإيجارات والديون الخاصة به.' : 'Commencez par ajouter votre premier client pour commencer à gérer ses locations et ses paiements.')}
          </p>
          <div className="flex justify-center gap-3">
            {hasActiveFilters ? (
              <button
                onClick={() => {
                  setSearch('');
                  setFilterCredit('all');
                }}
                className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold rounded-xl transition"
              >
                {isAr ? 'إزالة الفلاتر' : 'Réinitialiser les filtres'}
              </button>
            ) : (
              <button
                onClick={() => { setEditing(null); setShowForm(true); }}
                className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl shadow transition"
              >
                {isAr ? 'إضافة عميل جديد' : 'Ajouter un client'}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm table-container">
          <div className="overflow-x-auto w-full scrollbar-thin">
            <table className="w-full min-w-[800px] border-collapse">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['col_name','col_cin','col_phone','col_permis','col_address', 'col_credit', 'col_documents'].map(k => (
                    <th key={k} className={`${isAr ? 'text-right' : 'text-left'} px-5 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider`}>
                      {t(k)}
                    </th>
                  ))}
                  <th className={`${isAr ? 'text-left' : 'text-right'} px-5 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider`}>
                    {t('col_actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50/60 transition-colors duration-150">
                    {/* Name */}
                    <td className="px-5 py-4 font-semibold text-gray-900">{c.name}</td>
                    
                    {/* CIN */}
                    <td className="px-5 py-4 text-sm text-gray-650 font-medium">{c.cin}</td>
                    
                    {/* Phone */}
                    <td className="px-5 py-4 text-sm text-gray-600 font-mono font-medium">{c.phone}</td>
                    
                    {/* Permis */}
                    <td className="px-5 py-4 text-sm text-gray-650 font-medium">{c.permis}</td>
                    
                    {/* Address */}
                    <td className="px-5 py-4 text-sm text-gray-500 max-w-[180px] truncate" title={c.address}>
                      {c.address || '-'}
                    </td>
                    
                    {/* Balance */}
                    <td className="px-5 py-4"> 
                      {c.creditBalance > 0 ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-rose-50 text-rose-700 border border-rose-100 text-xs font-bold rounded-lg shadow-sm">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          {c.creditBalance.toLocaleString()} MAD
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-100 text-xs font-bold rounded-lg shadow-sm">
                          <Check className="w-3 h-3" />
                          0 MAD
                        </span>
                      )}
                    </td>
                    
                    {/* Documents */}
                    <td className="px-5 py-4">
                      <div className="flex gap-2 items-center">
                        {/* CIN Recto + Verso */}
                        {(c.docCniRecto || c.docCni) && (
                          <button 
                            onClick={() => setViewDoc({ url: c.docCniRecto || c.docCni, title: (isAr ? 'CIN الوجه' : 'CIN Recto') })} 
                            className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100 hover:text-emerald-700 border border-emerald-100 transition shadow-sm" 
                            title={isAr ? 'بطاقة التعريف - الوجه' : 'CIN Recto'}
                          >
                            <FileBadge className="w-4 h-4" />
                          </button>
                        )}
                        {c.docCniVerso && (
                          <button 
                            onClick={() => setViewDoc({ url: c.docCniVerso, title: (isAr ? 'CIN الخلف' : 'CIN Verso') })} 
                            className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100 hover:text-emerald-700 border border-emerald-100 transition shadow-sm" 
                            title={isAr ? 'بطاقة التعريف - الخلف' : 'CIN Verso'}
                          >
                            <FileBadge className="w-4 h-4" />
                          </button>
                        )}
                        
                        {/* Permis Recto + Verso */}
                        {(c.docPermisRecto || c.docPermis) && (
                          <button 
                            onClick={() => setViewDoc({ url: c.docPermisRecto || c.docPermis, title: (isAr ? 'الرخصة الوجه' : 'Permis Recto') })} 
                            className="p-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 hover:text-blue-700 border border-blue-100 transition shadow-sm" 
                            title={isAr ? 'رخصة السياقة - الوجه' : 'Permis Recto'}
                          >
                            <Car className="w-4 h-4" />
                          </button>
                        )}
                        {c.docPermisVerso && (
                          <button 
                            onClick={() => setViewDoc({ url: c.docPermisVerso, title: (isAr ? 'الرخصة الخلف' : 'Permis Verso') })} 
                            className="p-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 hover:text-blue-700 border border-blue-100 transition shadow-sm" 
                            title={isAr ? 'رخصة السياقة - الخلف' : 'Permis Verso'}
                          >
                            <Car className="w-4 h-4" />
                          </button>
                        )}
                        
                        {/* Passport */}
                        {c.docPassport && (
                          <button 
                            onClick={() => setViewDoc({ url: c.docPassport, title: t('passport_label') })} 
                            className="p-1.5 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 hover:text-purple-700 border border-purple-100 transition shadow-sm" 
                            title={t('view_passport')}
                          >
                            <Globe className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                    
                    {/* Actions */}
                    <td className="px-5 py-4">
                      <div className={`flex ${isAr ? 'justify-start' : 'justify-end'} items-center gap-2`}>
                        {/* History */}
                        <button 
                          onClick={() => setHistoryClient(c)} 
                          className="text-indigo-600 bg-indigo-50 border border-indigo-100 hover:bg-indigo-100/70 p-2 rounded-xl transition shadow-sm"
                          title={isAr ? 'سجل العميل' : 'Historique'}
                        >
                          <History className="w-4 h-4" />
                        </button>
                        
                        {/* Record Payment */}
                        {c.creditBalance > 0 && (
                          <button 
                            onClick={() => setPaymentClient(c)} 
                            className="text-emerald-600 bg-emerald-50 border border-emerald-100 hover:bg-emerald-100/70 p-2 rounded-xl transition shadow-sm animate-pulse" 
                            title={t('register_payment')}
                          >
                            <Coins className="w-4 h-4" />
                          </button>
                        )}
                        
                        {/* Edit */}
                        <button 
                          onClick={() => { setEditing(c); setShowForm(true); }}
                          className="text-gray-500 bg-gray-50 border border-gray-200 hover:bg-gray-100 p-2 rounded-xl transition shadow-sm"
                          title={isAr ? 'تعديل' : 'Modifier'}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        
                        {/* Delete */}
                        <button 
                          onClick={() => handleDelete(c.id)}
                          className="text-rose-600 bg-rose-50 border border-rose-100 hover:bg-rose-100 p-2 rounded-xl transition shadow-sm"
                          title={isAr ? 'حذف' : 'Supprimer'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <ClientForm 
          client={editing} 
          onSave={handleSave} 
          onClose={() => { setShowForm(false); setEditing(null); }} 
        />
      )}
      
      {paymentClient && (
        <ClientPaymentModal 
          client={paymentClient} 
          onSave={refresh} 
          onClose={() => setPaymentClient(null)} 
        />
      )}
      
      {historyClient && (
        <ClientHistoryModal 
          client={historyClient} 
          onClose={() => setHistoryClient(null)} 
        />
      )}
      
      {viewDoc && (
        <DocumentViewerModal 
          url={viewDoc.url} 
          title={viewDoc.title} 
          onClose={() => setViewDoc(null)} 
        />
      )}
    </div>
  );
}
